import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';
import { useGlobalStore } from '../stores/globalStore';
import { argbToHex } from '../types/facility';
import type {
  TrayTicketRequest,
  TrayTicketDataResult,
  TrayTicketMenuCycleInfo,
  TrayTicketPatientListItem,
  TrayTicketDisplayOptions,
  TrayTicketGuest,
  TrayTicketColorConfig,
  ExplainerRequest,
  ExplainerRegenerateRequest,
  ExplainerResult,
} from '../types/trayTicket';

// ===== QUERY KEYS =====

export const trayTicketKeys = {
  all: ['trayTicket'] as const,
  menuCycle: (facilityId: number, date: string) =>
    [...trayTicketKeys.all, 'menuCycle', facilityId, date] as const,
  patientList: (facilityId: number, date: string, mealtime: number) =>
    [...trayTicketKeys.all, 'patientList', facilityId, date, mealtime] as const,
  data: (facilityId: number, date: string, mealtime: number, fKeyPatient?: number | null, unit?: string | null) =>
    [...trayTicketKeys.all, 'data', facilityId, date, mealtime, fKeyPatient ?? null, unit ?? null] as const,
  displayOptions: (facilityId: number) =>
    [...trayTicketKeys.all, 'displayOptions', facilityId] as const,
  guestSchedule: (facilityId: number, date: string) =>
    [...trayTicketKeys.all, 'guestSchedule', facilityId, date] as const,
  explainer: (facilityId: number, fKeyPatient: number, date: string, mealtime: number) =>
    [...trayTicketKeys.all, 'explainer', facilityId, fKeyPatient, date, mealtime] as const,
};

// ===== TRAY TICKET DATA (POST) =====

/**
 * Lightweight menu-cycle-only query. Used as stage 1 of the Tray
 * Ticket Visualizer's staged loader so cycle info appears before
 * the heavier list/detail calls. Hits GET /api/TrayTicket/MenuCycle
 * which only runs `GetMenuCycleInUseInfo` — no patient scan.
 */
/** The menu cycle a date falls on (the query `useTrayTicketMenuCycle` caches). */
export async function fetchTrayTicketMenuCycle(facilityId: number, date: string): Promise<TrayTicketMenuCycleInfo> {
  const { data } = await apiClient.get<TrayTicketMenuCycleInfo>(
    API.trayTicketMenuCycle(facilityId),
    { params: { date } },
  );
  return data;
}

export function useTrayTicketMenuCycle(facilityId: number, date: string, enabled: boolean = true) {
  return useQuery({
    queryKey: trayTicketKeys.menuCycle(facilityId, date),
    queryFn: () => fetchTrayTicketMenuCycle(facilityId, date),
    enabled: enabled && facilityId > 0 && !!date,
    // Which menu cycle a date falls on doesn't change while a meal
    // service is running. The Visualizer no longer drops this cache
    // when it closes, so bound how long a reopen may trust it rather
    // than leaving it stale-on-arrival and refetching every launch.
    staleTime: 5 * 60 * 1000,
    // Explainer + Visualizer explicitly manage when this fires
    // (Refresh button, debounced key change). No auto refetch on
    // window focus, tab reconnect, or component remount when a
    // cached value already exists.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

/**
 * Bare-minimum patient list for the visualizer's resident dropdown.
 * Three fields per row, no LEFT JOINs, no per-row wrapper calls —
 * meant to return in milliseconds so the dropdown populates fast.
 */
export function useTrayTicketPatientList(
  facilityId: number,
  date: string,
  mealtime: number,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: trayTicketKeys.patientList(facilityId, date, mealtime),
    queryFn: async () => {
      const { data } = await apiClient.get<TrayTicketPatientListItem[]>(
        API.trayTicketPatientList(facilityId),
        { params: { date, mealtime } },
      );
      return data;
    },
    // Mealtime is 0-indexed (0 = Breakfast) so `mealtime > 0`
    // would silently skip Breakfast — gate on >= 0 instead.
    enabled: enabled && facilityId > 0 && !!date && mealtime >= 0,
    // Who is on the tray for a given date + mealtime is stable over
    // the span of a service. Short window so an admission or
    // discharge still surfaces quickly, but long enough that walking
    // resident-by-resident through the Visualizer doesn't refetch
    // the whole roster on every open. Refresh forces it regardless.
    staleTime: 60 * 1000,
    // User-initiated fetches only (Refresh button / debounced
    // toolbar change) — no auto refetch on focus / reconnect /
    // remount when cached data exists.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

export function useTrayTicketData(facilityId: number, request: TrayTicketRequest) {
  return useQuery({
    queryKey: trayTicketKeys.data(facilityId, request.date, request.mealtime, request.fKey_Patient, request.unit),
    queryFn: async () => {
      const { data } = await apiClient.post<TrayTicketDataResult>(
        API.trayTicketData(facilityId),
        request,
      );
      return data;
    },
    enabled: facilityId > 0 && !!request.date,
    // User-initiated fetches only — Visualizer's Refresh button
    // + debounced toolbar changes control when this fires.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
}

// ===== DISPLAY OPTIONS =====

export function useTrayTicketDisplayOptions(facilityId: number) {
  return useQuery({
    queryKey: trayTicketKeys.displayOptions(facilityId),
    queryFn: async () => {
      const { data } = await apiClient.get<TrayTicketDisplayOptions>(
        API.trayTicketDisplayOptions(facilityId),
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: facilityId > 0,
  });
}

export function useSaveTrayTicketDisplayOptions(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (options: TrayTicketDisplayOptions) => {
      await apiClient.post(API.trayTicketDisplayOptions(facilityId), options);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trayTicketKeys.displayOptions(facilityId) });
    },
  });
}

// ===== EXPLAINER =====

export function useTrayTicketExplainer(facilityId: number, request: ExplainerRequest | null) {
  return useQuery({
    queryKey: trayTicketKeys.explainer(facilityId, request?.fKey_Patient ?? 0, request?.date ?? '', request?.mealtime ?? 0),
    queryFn: async () => {
      const { data } = await apiClient.post<ExplainerResult>(
        API.trayTicketExplainer(facilityId),
        request,
      );
      return data;
    },
    enabled: facilityId > 0 && !!request && request.fKey_Patient > 0 && !!request.date,
    // Explainer 3-pass generator is expensive — fires ONLY on
    // initial load + explicit user action (Refresh button, or
    // toolbar change producing a new queryKey). Never on window
    // focus, tab reconnect, remount, or stale-time expiry.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
  });
}

export function useRegenerateExplainer(facilityId: number) {
  return useMutation({
    mutationFn: async (request: ExplainerRegenerateRequest) => {
      const { data } = await apiClient.post<ExplainerResult>(
        API.trayTicketExplainerRegenerate(facilityId),
        request,
      );
      return data;
    },
  });
}

// ===== GUEST SCHEDULE =====

export function useTrayTicketGuestSchedule(facilityId: number, date: string) {
  return useQuery({
    queryKey: trayTicketKeys.guestSchedule(facilityId, date),
    queryFn: async () => {
      const { data } = await apiClient.get<TrayTicketGuest[]>(
        API.trayTicketGuestSchedule(facilityId),
        { params: { date } },
      );
      return data;
    },
    enabled: facilityId > 0 && !!date,
  });
}

// ===== COLOR CONFIG (derived from facility settings) =====

export function useTrayTicketColorConfig(): TrayTicketColorConfig | null {
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);

  return useMemo(() => {
    if (!facilitySettings) return null;
    return {
      useColor: facilitySettings.use_Color ?? false,
      labelBg: argbToHex(facilitySettings.color_TT_Label_B_Argb ?? 0),
      labelFg: argbToHex(facilitySettings.color_TT_Label_F_Argb ?? 0),
      backgroundLabels: argbToHex(facilitySettings.color_TT_Background_Labels_Argb ?? 0),
      allergiesFg: argbToHex(facilitySettings.color_Allergies_Argb ?? 0),
      allergiesBg: argbToHex(facilitySettings.color_Allergies_B_Argb ?? 0),
      specialDevices: argbToHex(facilitySettings.color_Special_Devices_Argb ?? 0),
      dislikes: argbToHex(facilitySettings.color_Dislikes_Argb ?? 0),
      likes: argbToHex(facilitySettings.color_TT_Likes_Argb ?? 0),
      notes: argbToHex(facilitySettings.color_TT_Notes_Argb ?? 0),
      specialNeeds: argbToHex(facilitySettings.color_TT_SpecialNeeds_Argb ?? 0),
      facilityType: argbToHex(facilitySettings.color_Facility_Type_Argb ?? 0),
      portionSmall: argbToHex(facilitySettings.color_Portion_Small_Argb ?? 0),
      portionLarge: argbToHex(facilitySettings.color_Portion_Large_Argb ?? 0),
      portionDouble: argbToHex(facilitySettings.color_Portion_Double_Argb ?? 0),
      portionDoubleEntree: argbToHex(facilitySettings.color_Portion_Double_Entree_Argb ?? 0),
      locationFg: argbToHex(facilitySettings.color_TT_Location ?? 0),
      locationBg: argbToHex(facilitySettings.color_TT_Location_Back ?? 0),
      dietOverrideDate: argbToHex(facilitySettings.color_TT_DietOverrideDate ?? 0),
      mealItemNotes: argbToHex(facilitySettings.color_TT_MealItemNotes ?? 0),
      mealtimeTT: argbToHex(facilitySettings.color_Mealtime_TT_Argb ?? 0),
      transparencyLevel: facilitySettings.color_Transparency_Level ?? 255,
      colorConsistencies: facilitySettings.tT_ColorConsistencies ?? false,
    };
  }, [facilitySettings]);
}
